<template>
  <BasePhoneRow class="pre-project-list-item" opens-dialog @open="$emit('open')">
    <template #title>
      <span class="base-phone-row__title">{{ preProject.title }}</span>
    </template>

    <p v-if="contextLine" class="base-phone-row__line pre-project-list-item__context">{{ contextLine }}</p>

    <div v-if="hasTags" class="base-phone-row__meta">
      <BaseTag v-if="isNew" tone="info" preset="primary">{{ $t('tags.new') }}</BaseTag>
      <BaseTag v-if="hasNumber" tone="neutral" preset="soft">#{{ preProject.projectNumber }}</BaseTag>
      <BaseTag v-if="preProject.tenantId" preset="soft" tone="info" icon="user">
        {{ $t('page.pre-projects.source.manual') }}
      </BaseTag>
      <!-- Saving is in the details; the row says whether it's saved, as the desktop's lit
           save button does. -->
      <span
        v-if="preProject.followed"
        class="pre-project-list-item__saved"
        role="img"
        :aria-label="$t('page.pre-projects.follow-tooltip-subtitle')"
      >
        <BaseIcon name="bookmark" aria-hidden="true" />
      </span>
    </div>

    <ul v-if="budgetLines.length" class="pre-project-list-item__budgets">
      <li v-for="line in budgetLines" :key="line.key">
        <span class="pre-project-list-item__year">{{ line.when }}</span>
        <BaseTag v-if="line.period" preset="soft" tone="neutral">{{ line.period }}</BaseTag>
        <span class="pre-project-list-item__amount">{{ line.amount }}</span>
      </li>
    </ul>

    <!-- Sharing with the organisation, a pre-project's usual next step, from the row. -->
    <template v-if="actionable" #actions>
      <button
        type="button"
        class="base-phone-row__action"
        :class="{ 'is-active': preProject.shared }"
        :aria-label="preProject.shared ? $t('actions.unshare') : $t('actions.share')"
        :aria-pressed="String(!!preProject.shared)"
        :disabled="sharePending"
        @click.stop="$emit('share')"
      >
        <BaseIcon name="share-2" aria-hidden="true" />
      </button>
    </template>
  </BasePhoneRow>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BasePhoneRow from '@/components/base/BasePhoneRow.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import { locationDisplayName, locationsOf } from '@/util/location-util';
import { budgetLines, hasProjectNumber, isNewPreProject } from '@/util/pre-project-util';

// One pre-project in the phone list: what it is, who for, where, and its budgets for the
// years shown. The rest is in its details sheet.
export default {
  name: 'PreProjectListItem',
  components: { BaseIcon, BasePhoneRow, BaseTag },
  props: {
    preProject: {
      type: Object,
      required: true,
    },
    // `{ year, amount, period, hasAmount, span }` for each budget year shown, as the table's columns.
    budgets: {
      type: Array,
      default: () => [],
    },
    // Trial tenants can neither save nor share.
    actionable: {
      type: Boolean,
      default: true,
    },
    // Its sharing is under way.
    sharePending: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isNew() {
      return isNewPreProject(this.preProject);
    },
    hasNumber() {
      return hasProjectNumber(this.preProject);
    },
    hasTags() {
      return this.isNew || this.hasNumber || !!this.preProject.tenantId || !!this.preProject.followed;
    },
    // The emitter, then the first place it applies to and how many others.
    contextLine() {
      const locations = locationsOf(this.preProject);
      const where = locations.length
        ? [locationDisplayName(locations[0]), locations.length > 1 ? `+${locations.length - 1}` : '']
            .filter(Boolean)
            .join(' ')
        : '';
      return [this.preProject.displayEmitter, where].filter(Boolean).join(' · ');
    },
    // Years without an amount stay in the details.
    budgetLines() {
      return budgetLines(this.budgets.filter((budget) => budget.hasAmount));
    },
  },
};
</script>

<style lang="scss">
// The row is BasePhoneRow's; here, what only a pre-project shows.
.pre-project-list-item {
  &__saved {
    display: inline-flex;
    color: $color-accent;

    .base-icon {
      width: 14px;
      height: 14px;
    }
  }

  // 6px under the line above, the tags or the context alike (BasePhoneRow).
  &__budgets {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 6px 0 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
    }
  }

  &__year {
    font-size: 12px;
    color: $color-slate-500;
  }

  &__amount {
    color: $color-text-body;
    font-variant-numeric: tabular-nums;
  }
}
</style>
